import { useTheme as useMeridianTheme } from '@amzn/meridian/theme';
import { css } from '@emotion/css';
import { createTransition, motionDurations, reducedMotionQuery } from '../theme/motion';

export default function useActionButtonStyles() {
  const meridianTheme = useMeridianTheme();
  const isDarkMode = meridianTheme.mode === 'dark';

  const baseButtonClass = css({
    display: 'inline-flex',
    flex: '0 0 auto',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    height: 36,
    padding: '0 14px',
    boxSizing: 'border-box',
    borderRadius: 10,
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    transition: createTransition(
      ['background-color', 'border-color', 'box-shadow', 'color', 'transform'],
      motionDurations.instant,
    ),
    '&:active:not(:disabled)': {
      transform: 'scale(0.97)',
    },
    '&:focus-visible': {
      outline: `2px solid ${meridianTheme('themeBorderFocus')}`,
      outlineOffset: 2,
    },
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.55,
    },
    [reducedMotionQuery]: { transition: 'none', '&:active:not(:disabled)': { transform: 'none' } },
  });

  const primaryButtonClass = css(baseButtonClass, {
    border: 0,
    backgroundColor: meridianTheme('themeActionPrimaryDefault'),
    backgroundImage: 'linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0) 100%)',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.18), 0 1px 2px rgba(2, 8, 14, 0.16)',
    color: meridianTheme('themeInkInverse'),
    '&:hover:not(:disabled)': {
      backgroundColor: meridianTheme('themeActionPrimaryHover'),
    },
    '&:active:not(:disabled)': {
      backgroundColor: meridianTheme('themeActionPrimaryPressed'),
    },
  });

  const secondaryButtonClass = css(baseButtonClass, {
    border: `1px solid ${meridianTheme('themeBorderSurface')}`,
    backgroundColor: meridianTheme('themeSurfaceDefault'),
    boxShadow: isDarkMode ? '0 1px 2px rgba(0, 0, 0, 0.3)' : '0 1px 2px rgba(2, 8, 14, 0.06)',
    color: meridianTheme('themeInkDefault'),
    '&:hover:not(:disabled)': {
      borderColor: meridianTheme('themeBorderDefault'),
      backgroundColor: meridianTheme('themeActionNeutralSelected'),
    },
  });

  const tertiaryButtonClass = css(baseButtonClass, {
    border: '1px solid transparent',
    backgroundColor: 'transparent',
    color: meridianTheme('themeInkDefault'),
    '&:hover:not(:disabled)': {
      backgroundColor: meridianTheme('themeActionNeutralSelected'),
    },
    '&:active:not(:disabled)': {
      backgroundColor: meridianTheme('themeActionNeutralHover'),
    },
  });

  const smallButtonClass = css({
    height: 30,
    padding: '0 10px',
    gap: 6,
    borderRadius: 8,
    fontSize: 13,
  });

  const iconButtonClass = css(tertiaryButtonClass, {
    width: 32,
    height: 32,
    padding: 0,
    color: meridianTheme('themeInkSubdued'),
    '&:hover:not(:disabled)': {
      backgroundColor: meridianTheme('themeActionNeutralSelected'),
      color: meridianTheme('themeInkDefault'),
    },
  });

  return { primaryButtonClass, secondaryButtonClass, tertiaryButtonClass, smallButtonClass, iconButtonClass };
}
